import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Dialog,
  ErrorState,
  Field,
  LoadingState,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import type { PatientSummary } from '@/features/patients/types';
import {
  useRegisterActions,
  useRegisterRecord,
  type RegisterAction,
  type RegisterKind,
} from '@/features/registers/api';
import { isApiError } from '@/lib/api';
import { PatientPicker } from '@/pages/queue/PatientPicker';
import { COMPLAINT_KIND } from './complaint-format';
import { ComplaintDetails, EventMessage } from './ComplaintParts';
import styles from './OperationalRecords.module.css';
import { CorrectionChanges, CorrectSection } from './RecordCorrect';
import {
  reasonOk as correctReasonOk,
  useCorrectForm,
} from './register-correct';
import { FieldInput } from './register-fields';
import {
  dayLabel,
  formatValue,
  shownStatus,
  toApiData,
  toFormValue,
  whenLabel,
  type FormValue,
} from './register-format';
import { actionsFor } from './register-hooks';

type Staff = {
  names: Map<string, string>;
  options: { value: string; label: string }[];
};

function useFail() {
  const toast = useToast();
  const { t } = useTranslation();
  return (err: unknown) =>
    toast.show({
      tone: 'error',
      title: t('errors.genericTitle'),
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
}

/** Record details, the actions this role may take, and the timeline. */
export function RecordDrawer({
  kind: k,
  id,
  initialAction,
  staff,
  onClose,
}: {
  kind: RegisterKind;
  id: string;
  initialAction?: RegisterAction;
  staff: Staff;
  onClose: () => void;
}) {
  const q = useRegisterRecord(k.kind, id);
  const { act, unarchive } = useRegisterActions(k.kind);
  const { hasPermission } = useSession();
  // RL-1826: who archives can bring a record back, with a reason.
  const canUnarchive = hasPermission('data_security:manage');
  const [restoring, setRestoring] = useState(false);
  const [reason, setReason] = useState('');
  // RL-1826: RECORD_INVALID names a field (e.g. `before` on Archive).
  const [fieldError, setFieldError] = useState<{
    field: string;
    message: string;
  } | null>(null);
  const toast = useToast();
  const fail = useFail();
  const formId = useId();
  const [action, setAction] = useState<RegisterAction | undefined>(
    initialAction,
  );
  const [values, setValues] = useState<Record<string, FormValue>>(() =>
    Object.fromEntries(
      (initialAction?.fields ?? []).map((f) => [f.key, toFormValue(f, null)]),
    ),
  );
  const [note, setNote] = useState('');
  const r = q.data;
  // RL-2018: staff who can write the register correct a live record's fields.
  const correcting = useCorrectForm(k, r, fail);
  const canCorrect = k.canWrite && !!r && !r.archivedAt;
  const saveCorrection = () =>
    void correcting.submit().then((saved) => {
      if (saved)
        toast.show({
          tone: 'success',
          title: 'Correction saved',
          body: 'The change and your reason are on the timeline.',
        });
    });
  const choose = (a: RegisterAction) => {
    setAction(a);
    setFieldError(null);
    setNote('');
    setValues(
      Object.fromEntries(
        a.fields.map((f) => [f.key, toFormValue(f, r?.data[f.key] ?? null)]),
      ),
    );
  };
  const submit = () => {
    if (!r || !action) return;
    const { data, invalid } = toApiData(action.fields, values);
    if (invalid)
      return toast.show({ tone: 'error', title: `Check ${invalid}` });
    if (action.noteRequired && !note.trim())
      return toast.show({ tone: 'error', title: 'Add a note' });
    act
      .mutateAsync({
        id: r.id,
        action: action.key,
        data: action.fields.length ? data : undefined,
        note: note.trim() || undefined,
        version: r.version,
      })
      .then((after) => {
        // RL-1826: "Archive records past the period" says how many went.
        const archived = after.events.at(-1)?.data?.archived;
        toast.show({
          tone: 'success',
          title: action.label,
          body:
            typeof archived === 'number'
              ? `${archived} ${archived === 1 ? 'record' : 'records'} archived`
              : undefined,
        });
        setFieldError(null);
        setAction(undefined);
      })
      .catch((err: unknown) => {
        const f = isApiError(err) ? err.problem?.field : undefined;
        if (
          isApiError(err) &&
          err.code === 'RECORD_INVALID' &&
          typeof f === 'string'
        )
          setFieldError({ field: f, message: err.message });
        else fail(err);
      });
  };
  const restore = () => {
    if (!r) return;
    unarchive
      .mutateAsync({ id: r.id, reason: reason.trim() })
      .then(() => {
        toast.show({ tone: 'success', title: 'Back in the live list' });
        setRestoring(false);
        setReason('');
      })
      .catch(fail);
  };
  const reasonOk = reason.trim().length >= 3 && reason.trim().length <= 500;
  // A field error for a record field the action does not ask (e.g. `keep`,
  // the rule's "Kept for") is shown above the action's own fields.
  const recordFieldError =
    fieldError && !action?.fields.some((f) => f.key === fieldError.field)
      ? fieldError.message
      : undefined;
  const available = r ? actionsFor(k, r) : [];
  // RL-1137: complaints show owner and deadlines in their own section.
  const complaint = k.kind === COMPLAINT_KIND;
  return (
    <Dialog
      open
      onClose={onClose}
      title={r ? `${r.number ?? k.label} · ${r.title}` : k.label}
      footer={
        correcting.open ? (
          <>
            <Button onClick={correcting.cancel}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!correctReasonOk(correcting.reason)}
              loading={correcting.pending}
              onClick={saveCorrection}
            >
              Save correction
            </Button>
          </>
        ) : restoring ? (
          <>
            <Button onClick={() => setRestoring(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!reasonOk}
              loading={unarchive.isPending}
              onClick={restore}
            >
              Bring back from the archive
            </Button>
          </>
        ) : action ? (
          <>
            <Button onClick={() => setAction(undefined)}>Cancel</Button>
            <Button
              variant={action.danger ? 'danger' : 'primary'}
              loading={act.isPending}
              onClick={submit}
            >
              {action.label}
            </Button>
          </>
        ) : undefined
      }
    >
      {q.isLoading && <LoadingState />}
      {q.error !== null && q.error !== undefined && (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      )}
      {r && (
        <div className={styles.form}>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Details</h3>
            <dl className={styles.kv}>
              <dt>Status</dt>
              <dd>
                <Badge tone={shownStatus(r).tone} dot={!r.closedAt}>
                  {shownStatus(r).label}
                </Badge>
                {r.archivedAt && (
                  <>
                    {' '}
                    <Badge>Archived</Badge>
                  </>
                )}
              </dd>
              {r.archivedAt && (
                <>
                  <dt>Archived</dt>
                  <dd>{whenLabel(r.archivedAt)}</dd>
                </>
              )}
              {r.patient && (
                <>
                  <dt>Patient</dt>
                  <dd>{r.patient.fullName}</dd>
                </>
              )}
              {r.branch && (
                <>
                  <dt>Branch</dt>
                  <dd>{r.branch.name}</dd>
                </>
              )}
              {k.fields.map((f) => (
                <FieldRow
                  key={f.key}
                  label={f.label}
                  value={formatValue(f, r.data[f.key], staff.names)}
                />
              ))}
              {r.dueAt && !complaint && (
                <>
                  <dt>Due</dt>
                  <dd className={r.overdue ? styles.late : undefined}>
                    {dayLabel(r.dueAt)}
                    {r.overdue ? ' · late' : ''}
                  </dd>
                </>
              )}
              {r.owner && !complaint && (
                <>
                  <dt>Owner</dt>
                  <dd>{r.owner.fullName}</dd>
                </>
              )}
              <dt>Recorded</dt>
              <dd>
                {whenLabel(r.createdAt)}
                {r.createdBy ? ` · ${r.createdBy.fullName}` : ''}
              </dd>
            </dl>
          </section>
          {complaint && <ComplaintDetails r={r} />}
          {k.rule && <p className={styles.note}>{k.rule}</p>}
          {correcting.open ? (
            <CorrectSection
              kind={k}
              form={correcting}
              idPrefix={`${formId}-correct`}
              staffOptions={staff.options}
            />
          ) : restoring ? (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>
                Bring back from the archive
              </h3>
              <p className={styles.note}>
                The record goes back to the live list. The reason is kept on its
                timeline.
              </p>
              <Field
                label="Reason"
                hint="3 to 500 characters, e.g. archived by mistake."
              >
                <textarea
                  className={styles.textarea}
                  value={reason}
                  maxLength={500}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Field>
            </section>
          ) : action ? (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>{action.label}</h3>
              {recordFieldError && (
                <p className={styles.late} role="alert">
                  {recordFieldError}
                </p>
              )}
              {action.fields.map((f) => (
                <FieldInput
                  key={f.key}
                  field={f}
                  idPrefix={formId}
                  value={values[f.key] ?? toFormValue(f, null)}
                  staffOptions={staff.options}
                  error={
                    fieldError?.field === f.key ? fieldError.message : undefined
                  }
                  onChange={(v) => {
                    setValues((s) => ({ ...s, [f.key]: v }));
                    if (fieldError?.field === f.key) setFieldError(null);
                  }}
                />
              ))}
              <Field label={action.noteRequired ? 'Note' : 'Note (optional)'}>
                <textarea
                  className={styles.textarea}
                  value={note}
                  maxLength={1000}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Field>
            </section>
          ) : (
            (available.length > 0 ||
              canCorrect ||
              (r.archivedAt && canUnarchive)) && (
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Actions</h3>
                <div className={styles.actionRow}>
                  {r.archivedAt && canUnarchive && (
                    <Button size="sm" onClick={() => setRestoring(true)}>
                      Bring back from the archive
                    </Button>
                  )}
                  {available.map((a) => (
                    <Button
                      key={a.key}
                      size="sm"
                      variant={
                        a.danger
                          ? 'danger'
                          : a.primary
                            ? 'primary'
                            : 'secondary'
                      }
                      onClick={() => choose(a)}
                    >
                      {a.label}
                    </Button>
                  ))}
                  {canCorrect && (
                    <Button size="sm" onClick={correcting.start}>
                      Correct
                    </Button>
                  )}
                </div>
              </section>
            )
          )}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Timeline</h3>
            <ol className={styles.timeline}>
              {[...r.events].reverse().map((e) => (
                <li key={e.id}>
                  <span className={styles.dot} aria-hidden="true" />
                  <div className={styles.event}>
                    <b>{e.label}</b>
                    <span>
                      {whenLabel(e.at)}
                      {e.by ? ` · ${e.by.fullName}` : ''}
                    </span>
                    <CorrectionChanges e={e} kind={k} names={staff.names} />
                    {e.note && <p>{e.note}</p>}
                    {complaint && <EventMessage e={e} />}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>
      )}
    </Dialog>
  );
}

function FieldRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/** "Add …" drawer: patient (when the register needs one) and the kind's fields. */
export function RecordCreateDrawer({
  kind: k,
  staff,
  onClose,
  onCreated,
}: {
  kind: RegisterKind;
  staff: Staff;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { create } = useRegisterActions(k.kind);
  const toast = useToast();
  const fail = useFail();
  const formId = useId();
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [values, setValues] = useState<Record<string, FormValue>>(() =>
    Object.fromEntries(k.fields.map((f) => [f.key, toFormValue(f, null)])),
  );
  const submit = () => {
    if (k.patient === 'required' && !patient)
      return toast.show({ tone: 'error', title: 'Choose the patient' });
    const { data, invalid } = toApiData(k.fields, values);
    if (invalid)
      return toast.show({ tone: 'error', title: `Check ${invalid}` });
    create
      .mutateAsync({ data, patientId: patient?.id })
      .then((r) => {
        toast.show({ tone: 'success', title: `${k.noun} added` });
        onCreated(r.id);
      })
      .catch(fail);
  };
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={`Add ${k.noun}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {k.rule && <p className={styles.note}>{k.rule}</p>}
        {k.patient !== 'none' && (
          <PatientPicker
            value={patient}
            onChange={setPatient}
            label={k.patient === 'required' ? 'Patient' : 'Patient (optional)'}
          />
        )}
        {/* RL-1665 (RL-1945): a counter complaint reaches the patient's app. */}
        {k.kind === COMPLAINT_KIND && patient && (
          <p className={styles.note} role="note">
            {t('complaintForm.inAppNote')}
          </p>
        )}
        {k.fields.map((f) => (
          <FieldInput
            key={f.key}
            field={f}
            idPrefix={formId}
            value={values[f.key]}
            staffOptions={staff.options}
            onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
          />
        ))}
      </div>
    </Dialog>
  );
}
